import axios from 'axios'
import { v4 as uuidv4 } from 'uuid'
import { TOKEN } from '@/constants/storage'
import event from '@/event/index'
import { LOGOUT } from '@/event/modules/site'
import ElMessage from '@/utils/messsage'
import { deleteEmpty } from '@/utils/utils'
import Storage from './storage'

const SUCCESS_CODE = [200] // 响应成功
const GATEWAY_TOKEN_ILLEGES_CODES = [401] // token非法/已过期

/**
 * config 自定义配置项
 * @param withoutCheck 不使用默认的接口状态校验，直接返回 response
 * @param returnOrigin 是否返回整个 response 对象，为 false 只返回 response.data
 * @param showError 全局错误时，是否使用统一的报错方式
 * @param canEmpty 传输参数是否可以为空
 * @param timeout 接口请求超时时间，默认10秒
 * @param isCancelRequest 是否可以取消请求
 * @param isWhite 是否是白名单接口
 * @param finally 是否最终请求地址
 */
const customDefault = {
  showError: true,
  canEmpty: false,
  returnOrigin: false,
  withoutCheck: false,
  timeout: 15 * 1000,
  isCancelRequest: true,
  isWhite: false,
  finally: null,
  mergeHeaders: {},
}

// 创建请求器
const service = axios.create(
  Object.assign(
    {
      baseURL: '',
      responseType: 'json',
      headers: {
        'Content-Type': 'application/json;charset=utf-8',
      },
    },
    customDefault,
  ),
)

// 添加请求拦截器
service.interceptors.request.use(
  (request) => {
    // 对请求参数进行处理，清除空参数
    if (!request.canEmpty) {
      request.data && (request.data = deleteEmpty(request.data))
      request.params && (request.params = deleteEmpty(request.params))
    }
    // 请求头新增参数
    request.headers.serialId = uuidv4()
    // 合并请求头参数
    request.headers = Object.assign(request.headers, request.mergeHeaders)

    // 检测接口，根据环境自动切换前缀
    if (request.url.indexOf('http') === -1) {
      if (request.url[0] === '/') {
        request.url = request.url.substr(1)
      }
      request.url = `${request.finally || import.meta.env.VUE_APP_API_URL}/${request.url}`
    }

    request.headers.accessToken = Storage.get(TOKEN)

    return request
  },
  (error) => {
    ElMessage.error(error.toString())
    return Promise.reject(error)
  },
)

// 显示报错
const showError = (res) => {
  if (Object.prototype.toString.call(res) === '[object Error]') {
    // HTTP 请求失败
    ElMessage.error('API接口异常，请刷新重试')
  } else if (GATEWAY_TOKEN_ILLEGES_CODES.some((c) => res.data?.code === c)) {
    ElMessage.error('登录超时，请重新登录')
  } else {
    ElMessage.error(res?.data?.message || res?.data?.msg || '业务处理失败')
  }
}

// object对象存放每次new CancelToken生成的方法
let source = {}
// 每次请求前都会把path放在此数组中，响应成功后清除此请求path
let requestList = []

// 定义取消方法
function cancelRequest(path, allCancel) {
  // 请求列表里存在此path，即发起重复请求，把之前的请求取消掉
  if (path && requestList.includes(path) && typeof source[path] === 'function') {
    source[path](`终止请求: ${path}`)
  } else if (!path && allCancel) {
    // allCancel为true则请求列表里的请求全部取消
    requestList.forEach((el) => {
      source[el]('批量终止请求')
    })
  }
}

// 添加响应拦截器
service.interceptors.response.use(
  async (res) => {
    // 获取请求的api
    const path = JSON.stringify(res.config.url)
    // 请求完成后，将此请求从请求列表中移除
    requestList = requestList.filter((item) => !path.includes(item))

    if (SUCCESS_CODE.some((c) => res.data?.code === c)) {
      // 接口响应为请求成功
      if (res.config.returnOrigin) {
        return res.data
      }
      if (res.data && res.data !== 0) {
        return res.data.data
      } else {
        return res.data || {}
      }
    } else if (res.config.withoutCheck) {
      return res.data
    } else {
      const TOKEN_EXPIRED = [205, 401]
      if (TOKEN_EXPIRED.includes(res.data?.code)) {
        event.emit(LOGOUT)
      } else {
        showError(res)
      }
      return Promise.reject(res)
    }
  },
  (error) => {
    // 非 2xx 状态入口

    /**
     * config配置项
     * @param withoutCheck 不使用默认的接口状态校验，直接返回 response
     * @param returnOrigin 是否返回整个 response 对象，为 false 只返回 response.data
     * @param showError  全局错误时，是否使用统一的报错方式
     */
    const { config, response } = error
    // 判断是取消的请求不要弹出错误提示
    if (error.message.startsWith('终止请求')) {
      throw new Error(error.message)
    }
    if (config?.withoutCheck) {
      // 不进行状态状态检测
      return Promise.reject(response)
    }
    // 避免错误提示提示多次
    ElMessage.closeAll()
    if (error.code === 'ECONNABORTED') {
      // 超时
      ElMessage.error('请求超时，请重试')
    } else if (!error.response) {
      // 前端异常
      ElMessage.error('请求失败，请检查您的网络连接')
    } else if (error.config.showError) {
      // 接口异常，且配置为使用统一错误提示
      showError(error)
    }
    return Promise.reject(error)
  },
)
// post、get封装
function requestFn(method, path, data = {}, options = {}) {
  options = {
    ...customDefault,
    ...options,
  }

  // 取消上一次请求
  if (requestList.length) {
    cancelRequest(path)
  }
  // 设置isCancelRequest为ture, 请求前将path推入requestList
  if (options.isCancelRequest) {
    requestList.push(path)
  }

  if (method === 'post') {
    return service.post(path, data, {
      cancelToken: new axios.CancelToken((c) => {
        source[path] = c
      }),
      ...options,
    })
  } else if (method === 'get') {
    return service.get(path, {
      params: data,
      cancelToken: new axios.CancelToken((c) => {
        source[path] = c
      }),
      ...options,
    })
  }
}

export const api = {
  axios: service, // 原始 axios 对象
  showError,

  // 重新封装 get 函数，统一使用方式
  get: (path, data, config) => requestFn('get', path, data, config),
  delete: (path, data, config) =>
    service.delete(path, {
      data,
      ...config,
    }),
  post: (path, data, config) => requestFn('post', path, data, config),
  put: (path, data, config) => service.put(path, data, config),
  patch: (path, data, config) => service.patch(path, data, config),
}

export default api
